import React from 'react';
import { cn } from '../../utils/cn';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** Icon element to display */
    icon: React.ReactNode;

    /** Optional text label */
    label?: string;

    /** Size: sm (32px) | md (40px) | lg (48px) */
    size?: 'sm' | 'md' | 'lg';

    /**
     * Variant:
     * - default: light bg (keeps existing behavior)
     * - dark: dark bg (keeps existing behavior)
     * - inherit: DO NOT set text color; inherit from parent (for glass triggers etc.)
     */
    variant?: 'default' | 'dark' | 'inherit';

    /** Shape for icon-only buttons: square | circle */
    shape?: 'square' | 'circle';

    /** Additional classes for the icon specifically */
    iconClassName?: string;
}

/**
 * IconButton - Unified button for icons and small actions.
 */
export const IconButton: React.FC<IconButtonProps> = ({
    icon,
    label,
    size = 'md',
    variant = 'default',
    shape = 'square',
    className,
    iconClassName,
    disabled = false,
    ...props
}) => {
    const isDark = variant === 'dark';
    const isInherit = variant === 'inherit';

    // Base layout and interactive styles
    const baseClasses = cn(
        "group inline-flex items-center justify-center transition-colors duration-200 shrink-0",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        !disabled && "cursor-pointer",
        isInherit
            ? // ✅ 继承父层颜色：不设置 text-xxx，只控制背景交互（仍只用你 token）
            "hover:bg-w05 active:bg-w1"
            : isDark
                ? "text-w6 hover:text-w hover:bg-w1 active:bg-w2"
                : "text-b6 hover:text-b hover:bg-b05 active:bg-b05 active:text-b"
    );

    // Padding and sizing logic
    const sizeConfig = {
        sm: {
            container: label ? 'h-8 px-2.5 gap-1.5' : 'w-8 h-8',
            icon: 'w-3.5 h-3.5'
        },
        md: {
            container: label ? 'h-10 px-3.5 gap-2' : 'w-10 h-10',
            icon: 'w-4 h-4'
        },
        lg: {
            container: label ? 'h-12 px-5 gap-2.5' : 'w-12 h-12',
            icon: 'w-5 h-5'
        }
    }[size];

    // Shape logic
    const shapeClass = label ? "rounded-full" : (shape === 'circle' ? "rounded-full" : "rounded-xl");

    return (
        <button
            className={cn(baseClasses, sizeConfig.container, shapeClass, className)}
            disabled={disabled}
            {...props}
        >
            {label && (
                <span className="text-[13px] whitespace-nowrap leading-none mb-[1px] pointer-events-none">
                    {label}
                </span>
            )}
            {React.isValidElement(icon)
                ? React.cloneElement(icon as React.ReactElement<any>, {
                    className: cn(
                        sizeConfig.icon,
                        // ✅ lucide 默认用 stroke="currentColor"，这里保证跟随按钮颜色
                        "transition-colors duration-200 pointer-events-none",
                        iconClassName
                    ),
                })
                : icon}
        </button>
    );
};

export default IconButton;